import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { BillingPlanIds, isStarterPlan, isXMigrationPlanCode } from '../../../../enums/billing-plan-ids';
import Global from '../../../../store/global-selectors';
import { useDoesSellerHavePromotionalOffer } from '../components/migrate-plan-modal/hooks/use-does-seller-have-promotional-offer';
import { findAnnualPlanId, findXMigrationAnnualPlanId } from '../utils/plan-utilities';

const DefaultPlanId = BillingPlanIds.Standard2025Q2.toString();
const XMigrationDefaultPlanId = BillingPlanIds.Standard50Migration.toString();

/**
 * Returns an array of recommended billing plan IDs based on priority:
 * 1. Promotional target (highest priority)
 * 2. Backend-controlled recommendedBillingPlanIds
 * 3. Hard-coded defaults for Free/Starter/Trial users
 * @param {object} params
 * @param {string | null} params.currentBillingPlanId
 * @param {boolean} params.showAnnualPlans
 * @param {import('../../../../prop-types/subscription-data-types.d.ts').BillingPlan[]} params.referencePlans
 * @returns {string[]} Array of recommended billing plan IDs
 */
export const useGetRecommendedPlanIds = ({ currentBillingPlanId, showAnnualPlans, referencePlans }) => {
  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);
  const isCurrentBillingPlanTrial = subscriptionData?.currentBillingPlan?.billingPlanId?.toString() === BillingPlanIds.Trial.toString();
  const promotionUpgrade = useSelector(Global.selectSellerPromotionUpgrade);
  const hasPromotionalOffer = useDoesSellerHavePromotionalOffer();

  return useMemo(() => {
    // Priority 1: Promotional targets (only if offer is available and not accepted)
    if (hasPromotionalOffer) {
      const plans = promotionUpgrade?.targetCoupon?.plans;
      if (!plans) return [];

      const currentPlanPrice = subscriptionData?.currentBillingPlan?.pricePerPeriod?.value ?? 0;
      const targetPlans = plans.filter((targetPlan) => targetPlan.basePlanPrice.value > currentPlanPrice);
      const planIds = targetPlans.map((targetPlan) => targetPlan.billingPlanId);

      return planIds;
    }

    // Priority 2: Backend-controlled recommendations (convert numbers to strings)
    // Only show for Free/Starter/Trial — higher-tier plans already have a meaningful current plan
    const shouldShowRecommendation =
      !currentBillingPlanId ||
      currentBillingPlanId === BillingPlanIds.Free2025Q2.toString() ||
      isStarterPlan(currentBillingPlanId) ||
      isCurrentBillingPlanTrial;

    if (subscriptionData?.recommendedBillingPlanIds?.length > 0 && shouldShowRecommendation) {
      return subscriptionData.recommendedBillingPlanIds.map((id) => id.toString());
    }

    // Priority 3: Hard-coded defaults for specific plans
    if (!shouldShowRecommendation) {
      return [];
    }

    const isXMigrationProfile = referencePlans.some((plan) => isXMigrationPlanCode(plan.code));
    const monthlyDefaultId = isXMigrationProfile ? XMigrationDefaultPlanId : DefaultPlanId;
    const defaultId = showAnnualPlans
      ? ((isXMigrationProfile
          ? findXMigrationAnnualPlanId(monthlyDefaultId, referencePlans)
          : findAnnualPlanId(monthlyDefaultId, referencePlans)) ?? monthlyDefaultId)
      : monthlyDefaultId;
    return [defaultId];
  }, [
    currentBillingPlanId,
    hasPromotionalOffer,
    isCurrentBillingPlanTrial,
    promotionUpgrade,
    showAnnualPlans,
    subscriptionData?.currentBillingPlan?.pricePerPeriod?.value,
    subscriptionData?.recommendedBillingPlanIds,
    referencePlans,
  ]);
};
